import { useEffect, useRef } from 'react';
import api from '@/store/api';

/**
 * useDashboardSync — proactive UI sync hooks for the Dashboard.
 *
 * Three behaviours, encapsulated so DashboardView stays focused on layout:
 *
 *   1. `useProfileFocusRefresh(setUser)` — pulls `/api/profile` on mount
 *      and again every time the tab/window regains focus
 *      (`focus` + `visibilitychange`). Ensures `user.membershipActive`,
 *      `clubSubscriptionPaidThisMonth`, KYC role flags, etc. reflect the
 *      DB whenever the user comes back to the app.
 *
 *   2. `useActivationPolling(user, setUser)` — short-window polling for
 *      Kompa Club activation. When `membershipActive=false`, we GET
 *      `/api/payments` once. If a Pago Móvil activation is sitting in a
 *      pending_* / processing state, we poll `/api/profile` every 10s
 *      up to 5 attempts. The moment `membershipActive` flips to true
 *      (webhook landed or /confirm finalised) we stop and update the
 *      context — the `ActivationCard` clears itself without the user
 *      doing anything.
 *
 *   3. `useGoalPaymentPolling(...)` — generic version of (2) for goal
 *      cuota payments (same shape). Not consumed by DashboardView yet
 *      but exported for future tabs that want the same behaviour.
 *
 * Defensive defaults: every fetch is best-effort, silent on failure,
 * cancels cleanly on unmount, and never re-renders when the user is
 * already in the desired terminal state.
 */

const POLL_INTERVAL_MS = 10_000;
const MAX_POLL_ATTEMPTS = 5;
const PENDING_STATUSES = ['pending_reference', 'pending_verification', 'processing'];

export function useProfileFocusRefresh(setUser) {
  // Refresh profile on mount + on focus / visibility regain.
  useEffect(() => {
    let cancelled = false;

    const refresh = () => {
      api('/api/profile')
        .then((fresh) => {
          if (cancelled) return;
          setUser((prev) => ({ ...prev, ...fresh }));
        })
        .catch(() => { /* silent — next focus event will retry */ });
    };

    refresh();
    const onFocus = () => refresh();
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [setUser]);
}

export function useActivationPolling(user, setUser) {
  // Stable reference to the user flag so the hook only restarts when the
  // truthy value flips (not on every parent re-render).
  const membershipActive = !!user?.membershipActive;
  const userId = user?.id || '';
  const setUserRef = useRef(setUser);
  setUserRef.current = setUser;

  useEffect(() => {
    if (membershipActive) return undefined;
    // No user logged in yet → nothing to poll.
    if (!userId) return undefined;

    let cancelled = false;
    let intervalId = null;
    let attempts = 0;

    const stop = () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const tick = async () => {
      attempts += 1;
      try {
        const fresh = await api('/api/profile');
        if (cancelled) return;
        if (fresh?.membershipActive) {
          setUserRef.current((prev) => ({ ...prev, ...fresh }));
          stop();
          return;
        }
      } catch { /* silent — retry next tick */ }
      if (attempts >= MAX_POLL_ATTEMPTS) stop();
    };

    // Detect a pending activation before opening the poll loop.
    api('/api/payments')
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        const hasPendingActivation = rows.some((p) =>
          p?.isActivation && PENDING_STATUSES.includes(p?.status)
        );
        if (!hasPendingActivation) return;
        // Immediate first attempt + scheduled retries (10s apart, 5 max).
        tick();
        intervalId = setInterval(tick, POLL_INTERVAL_MS);
      })
      .catch(() => { /* silent — no polling without payments visibility */ });

    return stop;
  }, [membershipActive, userId]);
}
